import { IntegrationType } from 'constants/IntegrationConstants';
import { createWorkflowActions } from '@tock/shared/workflow/actionCreators';
import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { ConnectExternalSystemRequest, UpsertExternalSystemAreasRequest } = protos;

/**
 * List Tripleseat Locations for a business public api key
 *
 * @returns {Object} A Redux TockApi action.
 */
export function listTripleseatLeadsLocations(publicKey: string): ApiAction {
  return {
    TockApi: {
      getListProto: `/integrations/tripleseat/locations?public_key=${publicKey}&tripleseat_integration_type=${IntegrationType.TRIPLESEAT_LEADS}`,
      type: AppConstants.TRIPLESEAT_LIST_LOCATIONS,
    },
  };
}

/**
 * List Tripleseat Event Locations for a business
 *
 * @returns {Object} A Redux TockApi action.
 */
export function listTripleseatEventLocations(): ApiAction {
  return {
    TockApi: {
      getListProto: `/integrations/tripleseat/locations?public_key=''&tripleseat_integration_type=${IntegrationType.TRIPLESEAT_EVENTS}`,
      type: AppConstants.TRIPLESEAT_LIST_LOCATIONS,
    },
  };
}

/**
 * List Tripleseat rooms for the business. This is used for the Tripleseat event integration.
 *
 * @returns {Object} A Redux TockApi action.
 */
export function listTripleseatRooms(): ApiAction {
  return {
    TockApi: {
      getListProto: `/integrations/tripleseat/rooms`,
      type: AppConstants.TRIPLESEAT_LIST_ROOMS,
    },
  };
}

/**
 * List Commerce7 locations using the given credentials.
 *
 * @returns {Object} A Redux TockApi action.
 */
export function listCommerce7LocationsUsingAuth(): ApiAction {
  return {
    TockApi: {
      postListProto: `/integrations/commerce7/locations`,
      request: ConnectExternalSystemRequest.fromObject({}),
      type: AppConstants.COMMERCE7_LIST_LOCATIONS,
    },
  };
}

export function listConnectedCommerce7Integrations(): ApiAction {
  return {
    TockApi: {
      getListProto: `/integrations/commerce7/connections`,
      type: AppConstants.COMMERCE7_LIST_CONNECTIONS,
    },
  };
}

/**
 * List WineDirect locations using the given credentials.
 *
 * @returns {Object} A Redux TockApi action.
 */
export function listWineDirectLocations(
  username: string,
  password: string,
  loginDomain: string
): ApiAction {
  return {
    TockApi: {
      postListProto: `/integrations/winedirect/locations`,
      request: ConnectExternalSystemRequest.fromObject({ username, password, loginDomain }),
      type: AppConstants.WINEDIRECT_LIST_LOCATIONS,
    },
  };
}

export function clearLocations(): BaseTockAction {
  return {
    type: AppConstants.CLEAR_LOCATIONS,
  };
}

/**
 * Get smart group integration tags available to a business
 *
 * @returns {Object} A Redux TockApi action.
 */
export function getIntegrationTags(system: dashboard.ExternalSystemType): ApiAction {
  return {
    TockApi: {
      getListProto: `/integrations/tags/${system}`,
      type: AppConstants.INTEGRATION_TAGS,
    },
  };
}

/**
 * Gets all the ExternalSystemAreas for the given integration
 *
 * @returns
 */
export function getExternalSystemAreaAssignements(): ApiAction {
  return {
    TockApi: {
      getListProto: '/integrations/external-system-area',
      type: AppConstants.GET_EXTERNAL_SYSTEM_AREAS,
    },
  };
}

/**
 * Create or update a list of ExternalSystemArea
 *
 * @param upsertExternalSystemAreasRequest
 * @returns
 */
export function upsertExternalSystemAreaAssignments(
  upsertExternalSystemAreasRequest: dashboard.IUpsertExternalSystemAreasRequest
): ApiAction {
  const upsertRequest = UpsertExternalSystemAreasRequest.fromObject(
    upsertExternalSystemAreasRequest
  );
  return {
    TockApi: {
      putListProto: '/integrations/external-system-area',
      request: upsertRequest,
      type: AppConstants.UPSERT_EXTERNAL_SYSTEM_AREAS,
    },
  };
}

export function importTripleseatBookings(): ApiAction {
  return {
    TockApi: {
      getProto: '/integrations/tripleseat/import-bookings',
      type: AppConstants.IMPORT_TRIPLESEAT_BOOKINGS,
      toast: `Import Tripleseat bookings started`,
    },
  };
}

export const { fetchWorkflowStatus, fetchWorkflowResult } = createWorkflowActions({
  statusType: AppConstants.IMPORT_TRIPLESEAT_BOOKINGS_WORKFLOW_STATUS,
  resultType: AppConstants.IMPORT_TRIPLESEAT_BOOKINGS_WORKFLOW_STATUS_RESULTS,
  errorType: AppConstants.IMPORT_TRIPLESEAT_BOOKINGS_WORKFLOW_STATUS_FAILED,
});
